import { useContext, useState, useEffect } from "react";

import {
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Typography,
} from "@mui/material";
import { NavLink as RouterLink } from "react-router-dom";
import { SidebarContext } from "src/contexts/SidebarContext";

import BrightnessLowTwoToneIcon from "@mui/icons-material/BrightnessLowTwoTone";
import LocalPharmacyTwoToneIcon from "@mui/icons-material/LocalPharmacyTwoTone";
import WorkIcon from "@mui/icons-material/Work";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import BallotTwoToneIcon from "@mui/icons-material/BallotTwoTone";
import TableChartTwoToneIcon from "@mui/icons-material/TableChartTwoTone";
import LabelIcon from "@mui/icons-material/Label";
import ScienceIcon from "@mui/icons-material/Science";
import Biotech from "@mui/icons-material/Biotech";

import {
  Assignment,
  BackupTable,
  BubbleChart,
  DynamicFeed,
  FileUpload,
  SettingsSystemDaydream,
  ImportExport,
  SupervisedUserCircle,
} from "@mui/icons-material";
import { Auth } from "aws-amplify";
import HideRoute from "src/HideRoute";

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(["color"])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  "transform",
                  "opacity",
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);

  const [user, setUserData] = useState({ name: "", role: "" });
  useEffect(() => {
    if (user.name === "") {
      Auth.currentAuthenticatedUser()
        .then((data) => {
          const userData = {
            name: data.attributes["name"],
            role: data.attributes["custom:role"],
          };
          setUserData(userData);
        })
        .catch((err) => console.log(err));
    }
  });

  return (
    <>
      <MenuWrapper>
        <List component="div" subheader={<Typography>TeaRetina</Typography>}>
          <SubMenuWrapper>
            <List component="div">
              <HideRoute
                component={
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/tearetina"
                      startIcon={<BrightnessLowTwoToneIcon />}
                    >
                      TeaRetina Analysis
                    </Button>
                  </ListItem>
                }
                categoryname="TeaRetina Dashboard"
                role={user.role}
              />
            </List>
          </SubMenuWrapper>
          <SubMenuWrapper>
            <List component="div">
              <HideRoute
                component={
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/selling-marks"
                      startIcon={<AnalyticsIcon />}
                    >
                      Selling Marks
                    </Button>
                  </ListItem>
                }
                categoryname="TeaRetina Dashboard"
                role={user.role}
              />
            </List>
          </SubMenuWrapper>
          <SubMenuWrapper>
            <List component="div">
              <HideRoute
                component={
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/tools/labeling"
                      startIcon={<LabelIcon />}
                    >
                      Labeling Tool
                    </Button>
                  </ListItem>
                }
                categoryname="TeaRetina Dashboard"
                role={user.role}
              />
            </List>
          </SubMenuWrapper>
          <SubMenuWrapper>
            <List component="div">
              <HideRoute
                component={
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/tools/annotation"
                      startIcon={<LabelIcon />}
                    >
                      Annotation Tool
                    </Button>
                  </ListItem>
                }
                categoryname="TeaRetina Dashboard"
                role={user.role}
              />
            </List>
          </SubMenuWrapper>
          <SubMenuWrapper>
            <List component="div">
              <HideRoute
                component={
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/tearetina/images"
                      startIcon={<LabelIcon />}
                    >
                      Images
                    </Button>
                  </ListItem>
                }
                categoryname="TeaRetina Images Dashboard"
                role={user.role}
              />
            </List>
          </SubMenuWrapper>
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/density-labeling"
                  startIcon={<ScienceIcon />}
                >
                  Density Labeling
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>

        <HideRoute
          component={
            <List component="div" subheader={<Typography>Labeling</Typography>}>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/labeling/upload"
                      startIcon={<FileUpload />}
                    >
                      File Upload
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/labeling/files/assigned"
                      startIcon={<Assignment />}
                    >
                      Assigned Files
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/labeling/files/all"
                      startIcon={<DynamicFeed />}
                    >
                      All Files
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="Labeling"
          role={user.role}
        />

        {/* Benchmark Section - Updated Structure */}
        <HideRoute
          component={
            <List
              component="div"
              subheader={<Typography>Benchmark</Typography>}
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/benchmark/analysis"
                      startIcon={<BrightnessLowTwoToneIcon />}
                    >
                      Benchmark Analysis
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/benchmark/models"
                      startIcon={<BubbleChart />}
                    >
                      Models
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/benchmark/results"
                      startIcon={<AnalyticsIcon />}
                    >
                      Benchmark Results
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/benchmark/jobs"
                      startIcon={<WorkIcon />}
                    >
                      Benchmark Jobs
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="Benchmark Dashboard"
          role={user.role}
        />

        <HideRoute
          component={
            <List
              component="div"
              subheader={<Typography>Deployments</Typography>}
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/deploy/deploy-models"
                      startIcon={<TableChartTwoToneIcon />}
                    >
                      Deploy models
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboards/deploy/jobs"
                      startIcon={<WorkIcon />}
                    >
                      Jobs
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="Benchmark Dashboard"
          role={user.role}
        />

        <HideRoute
          component={
            <List
              component="div"
              subheader={<Typography>File Management</Typography>}
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/file-management/catalogs"
                      startIcon={<BallotTwoToneIcon />}
                    >
                      Upload Catalogs
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/file-management/taster-notes"
                      startIcon={<BallotTwoToneIcon />}
                    >
                      Upload Taster Notes
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/file-management/batches"
                      startIcon={<BallotTwoToneIcon />}
                    >
                      Upload Label Files
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/file-management/market-report"
                      startIcon={<BallotTwoToneIcon />}
                    >
                      Upload Market Report
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/file-management/text-file-upload"
                      startIcon={<BallotTwoToneIcon />}
                    >
                      Upload Catalog Text Files
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="File Upload"
          role={user.role}
        />
        <HideRoute
          component={
            <List
              component="div"
              subheader={<Typography>Audit Operations</Typography>}
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/audit/view-upload-tasks"
                      startIcon={<TableChartTwoToneIcon />}
                    >
                      View File Upload Tasks
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/audit/user-actions"
                      startIcon={<TableChartTwoToneIcon />}
                    >
                      View User Actions
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="File Upload - View"
          role={user.role}
        />
        <HideRoute
          component={
            <List
              component="div"
              subheader={<Typography>User Management</Typography>}
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/user-management/view"
                      startIcon={<LocalPharmacyTwoToneIcon />}
                    >
                      Users
                    </Button>
                  </ListItem>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/user-management/user-groups"
                      startIcon={<LocalPharmacyTwoToneIcon />}
                    >
                      User Groups
                    </Button>
                  </ListItem>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/user-management/register-user"
                      startIcon={<SupervisedUserCircle />}
                    >
                      Registration
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="User Management"
          role={user.role}
        />
        <HideRoute
          component={
            <List
              component="div"
              subheader={<Typography>Control Center</Typography>}
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/control-center/environments"
                      startIcon={<SettingsSystemDaydream />}
                    >
                      Environments
                    </Button>
                  </ListItem>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/control-center/data-transfer"
                      startIcon={<ImportExport />}
                    >
                      Data Transfer
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="Control Center"
          role={user.role}
        />

        <HideRoute
          component={
            <List
              component="div"
              subheader={<Typography>Mixing Samples</Typography>}
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/mixing-dashboard"
                      startIcon={<Biotech />}
                    >
                      Mixing Samples
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="TeaRetina Dashboard"
          role={user.role}
        />
        <HideRoute
          component={
            <List
              component="div"
              subheader={<Typography>Lot Progress Dashboard</Typography>}
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/lot-progress"
                      startIcon={<Biotech />}
                    >
                      Lot Progress
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="TeaRetina Dashboard"
          role={user.role}
        />
        <HideRoute
          component={
            <List component="div" subheader={<Typography>Clients</Typography>}>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/clients/management"
                      startIcon={<LocalPharmacyTwoToneIcon />}
                    >
                      Client Management
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          }
          categoryname="Client Management"
          role={user.role}
        />
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
